import { parsePhoneNumberFromString } from "libphonenumber-js/min";

export interface ImportedLead {
  sellerName: string;
  phone: string;
  address: string;
  row: number;
}

export interface ImportIssue {
  row: number;
  source: string;
  reason: string;
}

export interface ParsedLeadList {
  leads: ImportedLead[];
  issues: ImportIssue[];
}

type LeadField = "sellerName" | "phone" | "address";

interface ParsedRow {
  fields: string[];
  error?: string;
}

interface SourceRow {
  row: number;
  source: string;
}

const headerAliases: Record<string, LeadField> = {
  name: "sellerName",
  seller: "sellerName",
  sellername: "sellerName",
  phone: "phone",
  phonenumber: "phone",
  mobile: "phone",
  cell: "phone",
  address: "address",
  property: "address",
  propertyaddress: "address"
};

function headerKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function parseDelimitedRow(source: string, separator: string): ParsedRow {
  const fields: string[] = [];
  let field = "";
  let inQuotes = false;
  let closedQuote = false;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];

    if (inQuotes) {
      if (character === '"') {
        if (source[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          inQuotes = false;
          closedQuote = true;
        }
      } else {
        field += character;
      }
      continue;
    }

    if (character === separator) {
      fields.push(field.trim());
      field = "";
      closedQuote = false;
      continue;
    }

    if (character === '"') {
      if (field.trim() || closedQuote) return { fields: [], error: "This row has a misplaced quote." };
      field = "";
      inQuotes = true;
      continue;
    }

    if (closedQuote) {
      if (/\s/.test(character)) continue;
      return { fields: [], error: "This row has text after a closing quote." };
    }

    field += character;
  }

  if (inQuotes) return { fields: [], error: "This row has an unclosed quote." };
  fields.push(field.trim());
  return { fields };
}

function hasUnquoted(source: string, separator: string): boolean {
  let inQuotes = false;
  for (let index = 0; index < source.length; index += 1) {
    if (source[index] !== '"') {
      if (source[index] === separator && !inQuotes) return true;
      continue;
    }
    if (inQuotes && source[index + 1] === '"') {
      index += 1;
      continue;
    }
    inQuotes = !inQuotes;
  }
  return false;
}

function detectSeparator(source: string): string | null {
  if (hasUnquoted(source, "\t")) return "\t";
  if (hasUnquoted(source, "|")) return "|";
  if (hasUnquoted(source, ",")) return ",";
  return null;
}

function mapHeaders(fields: string[]): Partial<Record<LeadField, number>> | null {
  const headers: Partial<Record<LeadField, number>> = {};
  for (const [index, field] of fields.entries()) {
    const mapped = headerAliases[headerKey(field)];
    if (mapped && headers[mapped] === undefined) headers[mapped] = index;
  }
  return headers.phone !== undefined && headers.address !== undefined ? headers : null;
}

function plausibleUsPhone(value: string): boolean {
  const parsed = parsePhoneNumberFromString(value, "US");
  return parsed?.country === "US" && parsed.isValid();
}

function validateLead(candidate: Omit<ImportedLead, "row">, sourceRow: SourceRow, result: ParsedLeadList): void {
  if (!plausibleUsPhone(candidate.phone)) {
    result.issues.push({ ...sourceRow, reason: "Enter a valid US phone number." });
    return;
  }
  if (candidate.address.length < 6) {
    result.issues.push({ ...sourceRow, reason: "Enter a property address with at least 6 characters." });
    return;
  }
  result.leads.push({ ...candidate, row: sourceRow.row });
}

function positionalLead(fields: string[]): Omit<ImportedLead, "row"> | null {
  if (fields.length === 2) return { sellerName: "", phone: fields[0], address: fields[1] };
  if (fields.length === 3) return { sellerName: fields[0], phone: fields[1], address: fields[2] };
  return null;
}

export function parseLeadList(input: string): ParsedLeadList {
  const result: ParsedLeadList = { leads: [], issues: [] };
  const rows: SourceRow[] = input
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((source, index) => ({ row: index + 1, source }))
    .filter(({ source }) => source.trim().length > 0);

  if (!rows.length) return result;

  const firstSeparator = detectSeparator(rows[0].source);
  if (!firstSeparator) {
    result.issues.push({ ...rows[0], reason: "Separate the phone and property address with a pipe, tab, or comma." });
    return result;
  }

  const firstParsed = parseDelimitedRow(rows[0].source, firstSeparator);
  if (firstParsed.error) {
    result.issues.push({ ...rows[0], reason: firstParsed.error });
    return result;
  }

  const headers = mapHeaders(firstParsed.fields);
  const dataRows = headers ? rows.slice(1) : rows;
  const separator = firstSeparator;

  for (const sourceRow of dataRows) {
    const parsed = parseDelimitedRow(sourceRow.source, separator);
    if (parsed.error) {
      result.issues.push({ ...sourceRow, reason: parsed.error });
      continue;
    }

    if (headers) {
      const phone = parsed.fields[headers.phone!]?.trim() ?? "";
      const address = parsed.fields[headers.address!]?.trim() ?? "";
      const sellerName = headers.sellerName === undefined ? "" : parsed.fields[headers.sellerName]?.trim() ?? "";
      validateLead({ sellerName, phone, address }, sourceRow, result);
      continue;
    }

    if (separator === ",") {
      result.issues.push({ ...sourceRow, reason: "Add a header row with Name, Phone, and Address before comma-separated leads." });
      continue;
    }

    const candidate = positionalLead(parsed.fields);
    if (!candidate) {
      result.issues.push({ ...sourceRow, reason: "Use Name | Phone | Address or Phone | Address." });
      continue;
    }
    validateLead(candidate, sourceRow, result);
  }

  return result;
}
